import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TProjectOverview, TOverviewCriterionStatus } from "@/zapi-frontend/stores/project";
import { SectionCard } from "./SectionCard";
import { criterionStatusStyle, STATUS_ORDER } from "./overviewStyles";

function StatusBadge({ status }: { status: TOverviewCriterionStatus }) {
  const { t } = useTranslation("common");
  const s = criterionStatusStyle[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] leading-[16px] font-medium",
        s.bg,
        s.text,
        s.border
      )}
    >
      <span className={cn("size-1.5 rounded", s.dot)} />
      {t(`status.${status}`)}
    </span>
  );
}

export function SuccessCriteriaBlock({ overview }: { overview: TProjectOverview }) {
  const { t } = useTranslation(["projectHome", "common"]);
  const { items } = overview.successCriteria;
  if (!items.length) return null;
  const sortedItems = [...items].sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
  const counts = STATUS_ORDER.map((status) => ({
    status,
    count: items.filter((i) => i.status === status).length
  })).filter((c) => c.count > 0);
  const passCount = items.filter((i) => i.status === "pass").length;

  const label = (
    <>
      <span>{t("projectHome:overview.successCriteria")}</span>
      <span className="flex items-center gap-0.5 text-[12px] leading-[13px] font-medium tracking-normal">
        <span className="text-innoveraZinc-900">{passCount}</span>
        <span className="text-innoveraZinc-400">/</span>
        <span className="text-innoveraZinc-400">{items.length}</span>
      </span>
    </>
  );

  const legend = (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
      {counts.map((c) => {
        const s = criterionStatusStyle[c.status];
        return (
          <span key={c.status} className="inline-flex items-center gap-2 text-[12px] font-medium text-innoveraZinc-600">
            <span className={cn("size-1.5 rounded", s.legendDot)} />
            {t("projectHome:overview.legendCount", { value: c.count, status: t(`common:status.${c.status}`) })}
          </span>
        );
      })}
    </div>
  );

  return (
    <SectionCard label={label} action={legend}>
      <div className="grid grid-cols-3 gap-2 [grid-auto-rows:auto_1fr_auto]">
        {sortedItems.map((item, i) => (
          <div
            key={i}
            className={cn(
              "row-span-3 grid [grid-template-rows:subgrid] gap-4 p-4 min-h-[180px] border border-transparent",
              criterionStatusStyle[item.status].card
            )}
          >
            <div className="flex flex-col gap-1 items-start">
              <span className="text-[14px] leading-[22px] font-medium tracking-[-0.14px] text-innoveraZinc-700">
                {item.name}
              </span>
              <StatusBadge status={item.status} />
            </div>
            <FigmaTypography variant="body-2" color="zinc600">{item.evidence}</FigmaTypography>
            {item.target && (
              <div className="flex flex-col gap-1 pt-4 border-t border-innoveraZinc-150">
                <span className="text-[10px] leading-4 uppercase tracking-[0.06em] text-innoveraZinc-500">
                  {t("projectHome:overview.target")}
                </span>
                <FigmaTypography component="span" variant="body-2" color="zinc700">{item.target}</FigmaTypography>
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
